// src/pages/CookiePolicyPage.jsx
import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm'; // <-- DŮLEŽITÉ: Import pluginu pro tabulky
import { LoaderCircle, AlertTriangle } from 'lucide-react';

const CookiePolicyPage = () => {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetch('/zasady-cookies.md')
      .then((response) => {
        if (!response.ok) throw new Error('Nelze načíst soubor');
        
        // Prevence pádu, pokud by to omylem vracelo HTML (React App index)
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('text/html')) {
          throw new Error('Soubor nenalezen - server vrátil index.html');
        }
        
        return response.text();
      })
      .then((text) => {
        setContent(text);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Chyba načítání markdownu:", err);
        setError(true);
        setLoading(false);
      });
  },[]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <LoaderCircle className="animate-spin text-blue-600 w-12 h-12" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] text-red-600">
        <AlertTriangle className="w-16 h-16 mb-4" />
        <h2 className="text-2xl font-bold mb-2">Dokument se nepodařilo načíst</h2>
        <p className="text-gray-600">Zkontrolujte prosím, zda soubor zasady-cookies.md existuje ve složce public.</p>
      </div>
    );
  }

  return (
    <div className="flex justify-center min-h-full p-4 pt-8 pb-16 bg-gray-50">
      <div className="w-full max-w-4xl bg-white rounded-2xl shadow-xl p-8 md:p-12">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]} // <-- Aktivace pluginu pro parsování tabulek
          components={{
            h1: ({ node, children, ...props }) => <h1 className="text-3xl font-bold text-gray-900 mb-6" {...props}>{children}</h1>,
            h2: ({ node, children, ...props }) => <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4 border-b pb-2" {...props}>{children}</h2>,
            h3: ({ node, children, ...props }) => <h3 className="text-xl font-medium text-gray-800 mt-6 mb-3" {...props}>{children}</h3>,
            p: ({ node, children, ...props }) => <p className="text-gray-600 mb-4 leading-relaxed" {...props}>{children}</p>,
            ul: ({ node, children, ...props }) => <ul className="list-disc pl-6 mb-4 text-gray-600 space-y-1" {...props}>{children}</ul>,
            ol: ({ node, children, ...props }) => <ol className="list-decimal pl-6 mb-4 text-gray-600 space-y-1" {...props}>{children}</ol>,
            li: ({ node, children, ...props }) => <li className="mb-1" {...props}>{children}</li>,
            a: ({ node, children, ...props }) => <a className="text-blue-600 hover:text-blue-800 underline" target="_blank" rel="noopener noreferrer" {...props}>{children}</a>,
            strong: ({ node, children, ...props }) => <strong className="font-semibold text-gray-800" {...props}>{children}</strong>,
            
            // --- PŘIDÁNO: Pravidla pro vykreslení tabulek ---
            table: ({ node, children, ...props }) => (
              <div className="overflow-x-auto mb-6">
                <table className="min-w-full text-left border-collapse border border-gray-200" {...props}>
                  {children}
                </table>
              </div>
            ),
            thead: ({ node, children, ...props }) => <thead className="bg-gray-100 text-gray-800" {...props}>{children}</thead>,
            tbody: ({ node, children, ...props }) => <tbody className="divide-y divide-gray-200" {...props}>{children}</tbody>,
            tr: ({ node, children, ...props }) => <tr className="hover:bg-gray-50 transition-colors" {...props}>{children}</tr>,
            th: ({ node, children, ...props }) => <th className="px-4 py-3 border border-gray-200 font-semibold" {...props}>{children}</th>,
            td: ({ node, children, ...props }) => <td className="px-4 py-3 border border-gray-200 text-gray-600 text-sm align-top" {...props}>{children}</td>
          }}
        >
          {content}
        </ReactMarkdown>
      </div>
    </div>
  );
};

export default CookiePolicyPage;